import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms'
import { ZxcvbnResult, zxcvbnAsync, zxcvbnOptions } from '@zxcvbn-ts/core'
import { Matcher } from '@zxcvbn-ts/core/dist/types'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'
import { matcherPwnedFactory } from '@zxcvbn-ts/matcher-pwned'
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, first, from, map, switchMap } from 'rxjs'
import { PasswordStrength } from '~app/shared/password/password-strength-meter/password-strength-meter.component'

const ZXCVBN_OPTIONS = {
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
}

export const PASSWORD_VALIDATOR_DEBOUNCE_MS = 300
export const VALID_TEST_PASSWORD = 'correct+horse-battery=staple'

export const getPasswordStrengthValidator = (): AsyncValidatorFn => {
  zxcvbnOptions.setOptions(ZXCVBN_OPTIONS)
  if (!zxcvbnOptions.matchers.pwned) {
    const matcherPwned = matcherPwnedFactory(fetch, zxcvbnOptions)
    zxcvbnOptions.addMatcher('pwned', matcherPwned)
  }

  const subject = new BehaviorSubject<string>('')

  const debouncedInput$ = subject.asObservable().pipe(
    distinctUntilChanged(),
    debounceTime(PASSWORD_VALIDATOR_DEBOUNCE_MS),
    switchMap(passwordCandidate => from(zxcvbnAsync(passwordCandidate))),
    map(validateStrength),
    first(),
  )
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    subject.next(control.value)
    return debouncedInput$
  }
}

export const getMatcherPwned = (): Matcher => {
  return zxcvbnOptions.matchers.pwned
}

const validateStrength = (strengthEvaluation: ZxcvbnResult): ValidationErrors | null => {
  let passwordStrength = PasswordStrength.Poor
  if (strengthEvaluation.score < 2) {
    passwordStrength = PasswordStrength.Poor
  } else if (strengthEvaluation.score < 3) {
    passwordStrength = PasswordStrength.Medium
  } else {
    // A password strength of 3 or 4 is strong, therefore this validation should pass
    return null
  }

  // Otherwise return a weak-password error containing zxcvbn's results
  // We can use the .feedback.warning, .crackTimeSeconds, and .feedback.suggestions
  // to display useful corrective actions for the user.
  return {
    weakPassword: {
      strength: passwordStrength,
      warning: strengthEvaluation.feedback.warning,
      suggestions: strengthEvaluation.feedback.suggestions.sort((suggestionA, suggestionB) => {
        // Sort function to force HaveIBeenPwned change password suggestion to top of feedback panel
        if (suggestionA === ZXCVBN_OPTIONS.translations.suggestions.pwned) {
          return -1
        } else if (suggestionB === ZXCVBN_OPTIONS.translations.suggestions.pwned) {
          return 1
        }
        return 0
      }),
    },
  }
}
